import {
  ArrowLeftOutlined,
  FileExcelOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FolderOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import App from 'antd/lib/app';
import React, { FC, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { API } from '../api';
import { PageHeader } from '../components/common/page-header';
import { Paths } from '../types/common';
import { aimanagerModule1Topics } from './aimanager-module1-data';
import { AssetKind, ModuleAsset } from './module1-data';

type AssetFilter = 'all' | 'video' | 'files';

const getViewUrl = (s3Key: string) =>
  `${Paths.AIMANAGER_MODULE1_VIEW}?key=${encodeURIComponent(s3Key)}`;

const getAssetLinkViewUrl = (assetLinkId: string) =>
  `${Paths.AIMANAGER_MODULE1_VIEW}?assetLinkId=${encodeURIComponent(
    assetLinkId
  )}`;

const isFileVisible = (file: ModuleAsset, assetFilter: AssetFilter) => {
  if (assetFilter === 'video') {
    return file.kind === 'video';
  }

  if (assetFilter === 'files') {
    return file.kind !== 'video';
  }

  return true;
};

const getKindLabel = (kind: AssetKind) => {
  switch (kind) {
    case 'video':
      return 'Video';
    case 'excel':
      return 'Spreadsheet';
    case 'document':
      return 'Document';
    case 'pdf':
      return 'PDF';
    case 'csv':
      return 'CSV';
    case 'text':
      return 'Text';
    default:
      return 'File';
  }
};

const getFileIcon = (kind: AssetKind) => {
  switch (kind) {
    case 'video':
      return <VideoCameraOutlined />;
    case 'excel':
      return <FileExcelOutlined />;
    case 'document':
      return <FileTextOutlined />;
    case 'pdf':
      return <FilePdfOutlined />;
    case 'csv':
    case 'text':
      return <FileTextOutlined />;
    default:
      return <FileOutlined />;
  }
};

const Shell = styled('section')`
  min-height: calc(100vh - 40px);
  padding: 28px 32px 52px;
  color: hsl(218 28% 94%);
  background: hsl(220 28% 7%);

  @media (max-width: 720px) {
    padding: 18px 14px 36px;
  }
`;

const Content = styled('div')`
  max-width: 1360px;
  margin: 0 auto;
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 18px 0 22px;
`;

const BackButton = styled('button')`
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 1px solid hsl(216 18% 28%);
  border-radius: 8px;
  color: hsl(214 24% 78%);
  background: hsl(220 26% 10%);
  cursor: pointer;

  &:hover {
    border-color: hsl(188 82% 48%);
    color: hsl(188 92% 58%);
    background: hsl(220 26% 13%);
  }
`;

const TitleBlock = styled('div')`
  min-width: 0;
`;

const Title = styled('h1')`
  margin: 0;
  overflow: hidden;
  color: hsl(216 33% 97%);
  font-size: 24px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: 0;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 720px) {
    font-size: 19px;
    white-space: normal;
  }
`;

const Breadcrumbs = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 6px;
  color: hsl(214 18% 62%);
  font-size: 13px;
`;

const BreadcrumbButton = styled('button')`
  padding: 0;
  border: 0;
  color: hsl(190 82% 64%);
  font: inherit;
  background: transparent;
  cursor: pointer;

  &:hover {
    color: hsl(190 92% 74%);
  }
`;

const List = styled('div')`
  overflow: hidden;
  border: 1px solid hsl(216 18% 21%);
  border-radius: 8px;
  background: hsl(220 26% 10%);
`;

const RowBase = styled('div')`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 132px 172px;
  align-items: center;
  gap: 24px;
  min-height: 66px;
  padding: 10px 22px;
  border-bottom: 1px solid hsl(216 18% 18%);

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 780px) {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    padding: 10px 14px;
  }
`;

const FolderRow = styled(RowBase.withComponent('button'))`
  width: 100%;
  border-top: 0;
  border-right: 0;
  border-left: 0;
  color: inherit;
  text-align: left;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: hsl(218 30% 14%);
  }
`;

const FileRow = styled(RowBase)`
  background: transparent;
`;

const NameCell = styled('div')`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 18px;
`;

const FolderIconWrap = styled('span')`
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 42px;
  color: hsl(45 92% 72%);
  font-size: 34px;
`;

const FileIconWrap = styled('span')<{ kind: AssetKind }>`
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 42px;
  color: ${({ kind }) =>
    kind === 'video'
      ? 'hsl(162 90% 50%)'
      : kind === 'excel' || kind === 'csv'
        ? 'hsl(137 62% 55%)'
        : kind === 'pdf'
          ? 'hsl(2 82% 66%)'
          : 'hsl(207 80% 66%)'};
  font-size: 25px;
`;

const ItemName = styled('span')`
  min-width: 0;
  overflow: hidden;
  color: hsl(218 30% 95%);
  font-size: 15px;
  font-weight: 600;
  line-height: 1.38;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 720px) {
    white-space: normal;
  }
`;

const MetaCell = styled('span')`
  color: hsl(214 14% 58%);
  font-size: 14px;
  text-align: right;

  @media (max-width: 780px) {
    display: none;
  }
`;

const Action = styled('a')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 14px;
  border: 1px solid hsl(188 80% 38%);
  border-radius: 7px;
  color: hsl(188 88% 65%);
  font-size: 13px;
  font-weight: 800;
  text-decoration: none;
  background: hsl(188 90% 20% / 0.18);

  &:hover {
    border-color: hsl(188 86% 55%);
    color: hsl(188 96% 78%);
    background: hsl(188 90% 26% / 0.3);
  }
`;

const ActionGroup = styled('div')`
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 780px) {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
`;

const ActionButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid hsl(216 18% 30%);
  border-radius: 7px;
  color: hsl(214 24% 82%);
  font-size: 13px;
  font-weight: 800;
  background: hsl(220 26% 12%);
  cursor: pointer;

  &:hover {
    border-color: hsl(188 86% 55%);
    color: hsl(188 96% 78%);
    background: hsl(188 90% 26% / 0.22);
  }
`;

const MaterialDetails = styled('section')`
  margin-bottom: 18px;
  padding: 18px 22px;
  border: 1px solid hsl(216 18% 21%);
  border-radius: 8px;
  background: hsl(220 26% 10%);
`;

const DetailBlock = styled('div')`
  & + & {
    margin-top: 16px;
  }
`;

const DetailTitle = styled('h2')`
  margin: 0 0 8px;
  color: hsl(188 88% 70%);
  font-size: 13px;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: 0;
`;

const DetailText = styled('p')`
  margin: 0;
  color: hsl(214 24% 82%);
  font-size: 14px;
  line-height: 1.65;
`;

const BenefitList = styled('ul')`
  display: grid;
  gap: 6px;
  margin: 0;
  padding-left: 18px;
  color: hsl(214 24% 82%);
  font-size: 14px;
  line-height: 1.55;
`;

const normalizeParams = (topic?: string, material?: string) => {
  const params: Record<string, string> = {};
  if (topic) params.topic = topic;
  if (material) params.material = material;
  return params;
};

export const AiManagerModule1Page: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { message } = App.useApp();
  const topicId = searchParams.get('topic');
  const materialId = searchParams.get('material');

  const selectedTopic = useMemo(
    () => aimanagerModule1Topics.find((topic) => topic.id === topicId) ?? null,
    [topicId]
  );
  const selectedMaterial = useMemo(
    () =>
      selectedTopic?.materials.find((material) => material.id === materialId) ??
      null,
    [materialId, selectedTopic]
  );
  const visibleFiles = useMemo(
    () =>
      selectedMaterial?.files.filter((file) => isFileVisible(file, 'all')) ??
      [],
    [selectedMaterial]
  );

  const openRoot = () => setSearchParams({});
  const openTopic = (id: string) => setSearchParams(normalizeParams(id));
  const openMaterial = (id: string) =>
    selectedTopic && setSearchParams(normalizeParams(selectedTopic.id, id));

  const copyParameterizedLink = useCallback(
    async (file: ModuleAsset) => {
      try {
        const response = await API.assetLinks.create({
          s3Key: file.s3Key,
          name: file.name,
          kind: file.kind,
          size: file.size,
          isAuthRequired: false,
        });
        const path = `${getAssetLinkViewUrl(
          response.data.id
        )}&from=${encodeURIComponent(Paths.AIMANAGER_MODULE1)}`;
        const link = `${window.location.origin}${path}`;

        await navigator.clipboard.writeText(link);
        message.success('Parameterized link copied');
      } catch (error) {
        message.error('Unable to generate parameterized link');
      }
    },
    [message]
  );

  const title =
    selectedMaterial?.name ?? selectedTopic?.name ?? 'AI Manager / Module 1';

  return (
    <Shell>
      <Content>
        <PageHeader>AI Manager Module 1</PageHeader>
        <Header>
          {(selectedTopic || selectedMaterial) && (
            <BackButton
              type="button"
              onClick={() =>
                selectedMaterial && selectedTopic
                  ? openTopic(selectedTopic.id)
                  : openRoot()
              }
              aria-label="Back"
            >
              <ArrowLeftOutlined />
            </BackButton>
          )}
          <TitleBlock>
            <Title>{title}</Title>
            <Breadcrumbs>
              <BreadcrumbButton type="button" onClick={openRoot}>
                Root
              </BreadcrumbButton>
              <span>/</span>
              <span>Module 1</span>
              {selectedTopic && (
                <>
                  <span>/</span>
                  <BreadcrumbButton
                    type="button"
                    onClick={() => openTopic(selectedTopic.id)}
                  >
                    {selectedTopic.number}
                  </BreadcrumbButton>
                </>
              )}
              {selectedMaterial && (
                <>
                  <span>/</span>
                  <span>{selectedMaterial.name}</span>
                </>
              )}
            </Breadcrumbs>
          </TitleBlock>
        </Header>

        {selectedMaterial &&
          (selectedMaterial.description ||
            (selectedMaterial.benefit &&
              selectedMaterial.benefit.length > 0)) && (
            <MaterialDetails>
              {selectedMaterial.description && (
                <DetailBlock>
                  <DetailTitle>Description</DetailTitle>
                  <DetailText>{selectedMaterial.description}</DetailText>
                </DetailBlock>
              )}
              {selectedMaterial.benefit &&
                selectedMaterial.benefit.length > 0 && (
                  <DetailBlock>
                    <DetailTitle>Benefit</DetailTitle>
                    <BenefitList>
                      {selectedMaterial.benefit.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </BenefitList>
                  </DetailBlock>
                )}
            </MaterialDetails>
          )}

        <List>
          {!selectedTopic &&
            aimanagerModule1Topics.map((topic) => (
              <FolderRow
                key={topic.id}
                type="button"
                onClick={() => openTopic(topic.id)}
              >
                <NameCell>
                  <FolderIconWrap>
                    <FolderOutlined />
                  </FolderIconWrap>
                  <ItemName>{topic.name}</ItemName>
                </NameCell>
                <MetaCell>{topic.date}</MetaCell>
                <MetaCell>{topic.time}</MetaCell>
              </FolderRow>
            ))}

          {selectedTopic &&
            !selectedMaterial &&
            selectedTopic.materials.map((material) => (
              <FolderRow
                key={material.id}
                type="button"
                onClick={() => openMaterial(material.id)}
              >
                <NameCell>
                  <FolderIconWrap>
                    <FolderOutlined />
                  </FolderIconWrap>
                  <ItemName>{material.name}</ItemName>
                </NameCell>
                <MetaCell>{material.type}</MetaCell>
                <MetaCell>{material.files.length} files</MetaCell>
              </FolderRow>
            ))}

          {selectedMaterial &&
            visibleFiles.map((file) => (
              <FileRow key={file.s3Key}>
                <NameCell>
                  <FileIconWrap kind={file.kind}>
                    {getFileIcon(file.kind)}
                  </FileIconWrap>
                  <ItemName>{file.name}</ItemName>
                </NameCell>
                <MetaCell>
                  {getKindLabel(file.kind)} · {file.size}
                </MetaCell>
                <ActionGroup>
                  <Action
                    href={`${getViewUrl(file.s3Key)}&from=${encodeURIComponent(
                      Paths.AIMANAGER_MODULE1
                    )}`}
                  >
                    Open
                  </Action>
                  <ActionButton
                    type="button"
                    onClick={() => copyParameterizedLink(file)}
                  >
                    Copy link
                  </ActionButton>
                </ActionGroup>
              </FileRow>
            ))}
        </List>
      </Content>
    </Shell>
  );
};
